<script setup>
import { ref, computed, defineEmits } from 'vue';
import { useTranslation } from '../composables/useTranslation';

const { t } = useTranslation();

const props = defineProps({
    modelValue: {
        type: Array,
        default: () => []
    },
    options: {
        type: Array,
        default: () => []
    },
    placeholder: {
        type: String,
        default: null
    }
});

const search = ref('');
const searchInput = ref(null);
const options = computed( () => {
    const s = search.value.toLowerCase();

    if ( s === '' ) return props.options;

    return props.options.filter( o => {
        return o.label.toString().toLowerCase().includes(s) || o.value.toString().toLowerCase().includes(s);
    })
})

const selectedOptions = computed( () => {
    return props.options.filter( o => props.modelValue.includes(o.value) );
});

const emit = defineEmits(['update:modelValue', 'select']);
const select = (value) => {
    if ( props.modelValue.includes(value) ) {
        // Add class to target option.removing
        props.modelValue = props.modelValue.filter( v => v !== value );
    } else {
        props.modelValue.push(value);
        search.value = '';
        focus();
    }

    emit('update:modelValue', props.modelValue);
    emit('select', props.modelValue);
}

const isOpen = ref(false);

const open = () => {
    isOpen.value = true;
    focus()
}

const close = () => {
    isOpen.value = false;
    search.value = '';
}

const focus = () => {
    setTimeout( () => {
        searchInput.value.focus();
    }, 50)
}

// Anywhere clicked but multiselect
const multiselect = ref(null);
document.addEventListener('click', (e) => {
    if ( multiselect.value && !multiselect.value.contains(e.target) ) {
        close();
    }
})

const removeLast = () => {
    if ( search.value === '' && props.modelValue.length ) {
        props.modelValue = props.modelValue.slice(0, -1);
        emit('update:modelValue', props.modelValue);
        emit('select', props.modelValue);
    }
}

</script>
<template>
    <div class="relative w-full outline-none" ref="multiselect" tabindex="0" @keydown.esc="close" :class="{'z-10' : isOpen}">
        <div @click="isOpen ? focus() : open()" class="w-full  ring-1 ring-gray-100 p-1.5 bg-white hover:shadow-sm rounded hover:ring-gray-300 flex items-center justify-start gap-1.5 flex-wrap">
            <div v-if="selectedOptions.length" v-for="o in selectedOptions" class="bounce-enter-active bg-white ring-1 ring-gray-200 text-gray-600 hover:ring-gray-300 shadow-sm h-8 pl-2 rounded flex items-center justify-between gap-2"
            >
                {{ o.label }}
                <div @click.prevent="select(o.value)" class="h-full w-12 flex items-center justify-center cursor-pointer text-gray-400 hover:text-red-500 transition active:scale-75 outline-none" tabindex="1">
                    <svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-5" viewBox="0 0 16 16">
                        <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708"/>
                    </svg>
                </div>
            </div>
            <input v-if="isOpen || !selectedOptions.length" type="text" @keydown.delete="removeLast" v-model="search" ref="searchInput" contenteditable="true" class="outline-none border-0 h-8 focus:ring-0 placeholder:text-sm placeholder:text-gray-400" 
            :placeholder="selectedOptions.length > 0 ? '' : (props.placeholder ?? t('select_pages'))"
            :class="{ 'w-20' : selectedOptions.length, 'w-fit' : !selectedOptions.length}"/>
        </div>

        <transition name="slide">
            <div v-if="isOpen">
                <div class="absolute z-30 opacity-100 translate-y-1 w-72 bg-white shadow-lg rounded border-gray-200 max-h-56 overflow-y-scroll scrollbar-thin scrollbar-track-slate-50 scrollbar-thumb-indigo-500">
                    <div v-if="options.length" v-for="o in options" @click.prevent="select(o.value)" class="flex group items-center justify-between cursor-pointer px-4 py-2.5 text-sm hover:bg-gray-50 transition mb-px border-b border-gray-50"
                    :class="{'text-indigo-600' : props.modelValue.includes(o.value)}">
                        {{ o.label || o.value }}
                        <svg :class="{ 'text-indigo-500' : props.modelValue.includes(o.value), 'text-transparent group-hover:text-gray-600' : !props.modelValue.includes(o.value) }" xmlns="http://www.w3.org/2000/svg" class="fill-current w-5 transition-all" viewBox="0 0 16 16">
                            <path v-if="props.modelValue.includes(o.value)" d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425z"/>
                            <path v-else d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4"/>
                        </svg>
                    </div>
                    <div v-else class="px-4 py-3 text-sm text-gray-400">{{ t('no_page_found') }}</div>
                </div>
            </div>
        </transition>
    </div>
</template>